/**
 * Items — vendor-side "My Items" tab. Shopify-style listing CRUD.
 *
 * Flow:
 *   1. List all items the vendor has created
 *   2. Click "Add Item" → AddItem form (new file) opens inline
 *   3. Click an existing item → same form opens pre-populated
 *   4. Delete / toggle active in list
 */
import { useCallback, useEffect, useState } from 'react';
import { Package, Plus, Pencil, Trash2, EyeOff, Eye, Lock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { GlassCard, API } from './GlassCard';
import AddItem from './AddItem';

function abs(url) {
  if (!url) return '';
  return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

export default function Items() {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readiness, setReadiness] = useState(null);
  const [editing, setEditing] = useState(null);  // null | 'new' | item object

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/items/mine`, { headers: getAuthHeaders() });
      if (r.ok) setItems(await r.json());
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchReadiness = useCallback(async () => {
    try {
      const r = await fetch(`${API}/completion/vendor`, { headers: getAuthHeaders() });
      if (r.ok) setReadiness(await r.json());
    } catch {}
  }, [getAuthHeaders]);

  useEffect(() => { fetchItems(); fetchReadiness(); }, [fetchItems, fetchReadiness]);

  const toggleActive = async (item) => {
    try {
      await fetch(`${API}/items/${item.item_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !item.is_active }),
      });
      fetchItems();
    } catch {
      toast.error('Could not update item');
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;
    try {
      await fetch(`${API}/items/${item.item_id}`, { method: 'DELETE', headers: getAuthHeaders() });
      toast.success('Item deleted');
      fetchItems();
    } catch {
      toast.error('Could not delete item');
    }
  };

  const canSell = readiness?.ready_to_sell !== false;

  if (editing) {
    return (
      <AddItem
        existing={editing === 'new' ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchItems(); }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="items-title">My Items</h1>
          <p className="text-sm text-[#9CA3AF] mt-1">Shopify-style item listings — shown in the public Shop and on your vendor profile</p>
        </div>
        {canSell ? (
          <button
            onClick={() => setEditing('new')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all text-sm"
            data-testid="add-item-btn"
          >
            <Plus size={16} /> Add Item
          </button>
        ) : (
          <button
            disabled
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white/60 bg-white/5 border border-white/10 cursor-not-allowed text-sm"
            title="Finish profile setup to list items"
            data-testid="add-item-btn-locked"
          >
            <Lock size={14} /> Add Item
          </button>
        )}
      </div>

      {!canSell && (
        <GlassCard className="p-5 flex items-start gap-3 border-amber-400/30 bg-amber-500/5">
          <Lock size={16} className="text-amber-300 mt-0.5" />
          <div>
            <p className="text-sm text-white font-semibold">Finish setting up your vendor profile first</p>
            <p className="text-xs text-[#9CA3AF] mt-1">Add a profile picture, bio and connect payouts before listing items for sale.</p>
          </div>
        </GlassCard>
      )}

      {loading ? (
        <div className="text-center py-12 text-sm text-[#9CA3AF]">Loading your items…</div>
      ) : items.length === 0 ? (
        <GlassCard className="text-center py-16 px-6" data-testid="items-empty">
          <Package size={48} className="mx-auto mb-4 text-[#9CA3AF] opacity-40" />
          <p className="text-white font-semibold">No items yet</p>
          <p className="text-[#9CA3AF] text-xs mt-1">Add your first product and it will appear in the public Shop within minutes.</p>
          {canSell && (
            <button
              onClick={() => setEditing('new')}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#0E0E23] text-sm font-semibold hover:bg-[#F4F4F7]"
            >
              Add your first item <ArrowRight size={14} />
            </button>
          )}
        </GlassCard>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="items-grid">
          {items.map((it) => {
            const img = (it.images || [])[0];
            const inventory = (it.variants || []).length > 0
              ? (it.variants || []).reduce((s, v) => s + (v.inventory || 0), 0)
              : (it.inventory || 0);
            return (
              <GlassCard key={it.item_id} className="overflow-hidden" data-testid={`item-${it.item_id}`}>
                <div className="relative aspect-video bg-[#1A1D4A] overflow-hidden">
                  {img ? (
                    <img src={abs(img)} alt={it.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#9CA3AF]">
                      <Package size={40} strokeWidth={1} />
                    </div>
                  )}
                  {!it.is_active && (
                    <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/60 text-white uppercase tracking-wider">
                      Hidden
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <p className="font-semibold text-white text-sm truncate">{it.title}</p>
                  <p className="text-[#9CA3AF] text-xs mt-0.5">
                    ${Number(it.base_price).toFixed(2)} · {inventory} in stock · {it.sales_count || 0} sold
                  </p>
                  <div className="mt-4 flex gap-1.5">
                    <button
                      onClick={() => setEditing(it)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white/5 border border-white/10 hover:bg-white/10"
                      data-testid={`item-edit-${it.item_id}`}
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => toggleActive(it)}
                      className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10"
                      data-testid={`item-toggle-${it.item_id}`}
                      title={it.is_active ? 'Hide from shop' : 'Show in shop'}
                    >
                      {it.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button
                      onClick={() => deleteItem(it)}
                      className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 hover:bg-red-500/20"
                      data-testid={`item-delete-${it.item_id}`}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
